import React, { useEffect, useState } from 'react';

import { Radio } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Select from 'components/lib/Select';

import { useCommunityViewWidgetModalContext } from './CommunityViewWidgetModalContext';
import { COMMUNITY_CONTRACT_SELECTION_TYPE, COMMUNITY_CONTRACT_TYPE } from '../../../constants';
import { CommunityContract } from '../../../types/contracts';
import { useCommunityViewContext } from '../CommunityViewContext';

const StyledSelect = styled(Select)`
  && {
    height: 36px;
    .ant-select-selector {
      height: 36px;
      border-radius: 0;
      border: 1px solid var(--line-gray);
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    }
    .ant-select-selection-item {
      font-size: 14px;
    }
  }
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-flow: column;
`;

const DropdownActionBar = styled.div`
  padding: 15px 20px;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  border-bottom: solid 1px var(--line-gray);
`;

const DropdownSelectionMode = styled.div`
  padding: 10px 0 14px 20px;
  display: flex;
  flex-flow: column;
  gap: 18px;
  border-bottom: solid 1px var(--line-gray);
`;

const DropdownTitle = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);
  letter-spacing: 0.5px;
`;

const RadioGroupHorizontal = styled(Radio.Group)`
  display: flex;
  flex-flow: row;
  gap: 20px;
`;

const RadioGroupVertical = styled(Radio.Group)`
  padding: 20px 20px 20px 40px;
  display: flex;
  flex-flow: column;
  gap: 20px;
`;

const Link = styled.span`
  color: var(--dark-blue);
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  pointer-events: cursor;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 185px;
`;

const Title = styled.div`
  color: var(--text-primary);
  font-size: 12px;
  margin-bottom: 5px;

  &.heading {
    font-family: var(--font-bold);
    font-size: 14px;
    margin-bottom: 10px;
  }
`;

const CommunityViewWidgetModalCommunityContractFilter = () => {
  const [open, setOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(1);
  const [selectedValue, setSelectedValue] = useState<number>(COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE);

  const {
    communityContractType,
    communityContractSelectionType,
    setCommunityContractType,
    setCommunityContractSelectionType,
  } = useCommunityViewWidgetModalContext();
  const { community } = useCommunityViewContext();

  const communityContractOptions: { label: string; value: number }[] = (community?.contracts ?? []).map(
    (communityContract: CommunityContract) => ({
      value: communityContract.id,
      label:
        communityContract.contract_id === COMMUNITY_CONTRACT_TYPE.RENTAL
          ? communityContract.name
          : `${communityContract.name} ${communityContract.refund_percentage}%`,
    })
  );

  useEffect(() => {
    if (communityContractType) {
      setSelectedValue(communityContractType);
    }
    if (communityContractSelectionType) {
      setSelectionMode(communityContractSelectionType);
    }
  }, [communityContractType, communityContractSelectionType]);

  const onSubmit = () => {
    setCommunityContractType(selectedValue);
    setCommunityContractSelectionType(selectionMode);
    setOpen(false);
  };

  const onReset = () => {
    setSelectionMode(COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE);
    if (communityContractOptions.length) {
      setSelectedValue(communityContractOptions[0].value);
    }
  };

  return (
    <Container>
      <Title>Contract Type</Title>
      <StyledSelect
        open={open}
        onDropdownVisibleChange={(visible) => setOpen(visible)}
        placement={'bottomLeft'}
        value={communityContractType}
        options={communityContractOptions}
        dropdownStyle={{
          padding: 0,
          borderRadius: 0,
          border: '1px solid var(--line-gray)',
          minWidth: 370,
        }}
        dropdownRender={() => (
          <DropdownContainer>
            <DropdownActionBar>
              <Link onClick={onReset}>Reset</Link>
              <Button type="primary" style={{ width: '96px', height: '36px' }} onClick={onSubmit}>
                Apply
              </Button>
            </DropdownActionBar>
            <DropdownSelectionMode>
              <DropdownTitle>SELECT A CONTRACT</DropdownTitle>
              <RadioGroupHorizontal onChange={(e: any) => setSelectionMode(e.target.value)} value={selectionMode}>
                <Radio value={1}>Approximate</Radio>
                <Radio value={2}>Exact</Radio>
              </RadioGroupHorizontal>
            </DropdownSelectionMode>
            <RadioGroupVertical onChange={(e: any) => setSelectedValue(e.target.value)} value={selectedValue}>
              {communityContractOptions.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </RadioGroupVertical>
          </DropdownContainer>
        )}
      />
    </Container>
  );
};

export default CommunityViewWidgetModalCommunityContractFilter;
